import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Tooltip
} from '@mui/material';
import { ArrowBack } from "@mui/icons-material";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Grid from '@mui/material/Grid2';

import AppContext from '../components/AppContext.js';
import config from '../config.js';
import DataLoader from '../components/common/DataLoader.js';
import MovimentsListEdit from '../components/common/MovimentsListEdit.js';
import MovimentsPendentsValidarList from '../components/common/MovimentsPendentsValidarList.js';
import AmazonReportModal from '../components/common/AmazonReportModal.js';
import { fetchListConceptes } from '../components/common/SelectConcepte.js';

const TractamentManual = () => {
  const { ctxApp, setCtxApp, ctxUser, conceptesNodesMap } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const [tabValue, setTabValue] = useState(ctxApp?.tractMensualTabSelected || 0);
  const [listConceptes, setListConceptes] = useState(null);
  const [id, setId] = useState(0);
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [amazonReport, setAmazonReport] = useState({
    open: false,
    reportHtml: '',
    reportSummary: '',
    updatedCount: 0
  });

  const navigate = useNavigate();
  const { userapp: paramUserapp } = useParams();
  const userapp = paramUserapp;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCtxApp(prev => ({ ...prev, tractMensualTabSelected: newValue }));
  };

  const gotoDashboard = () => {
    navigate(`/${userapp}`);
  };

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};

    const getEnsHoDeuenMovs = async () => {
      try {
        let nodeId = null;
        if (conceptesNodesMap && conceptesNodesMap.size > 0) {
          const found = Array.from(conceptesNodesMap.values()).find(n =>
            n.codi === 'PEND_ENS_HO_DEUEN' ||
            (n.conceptePath && n.conceptePath.toUpperCase() === 'PENDENT/PEND_ENS_HO_DEUEN') ||
            (n.nom && n.nom.toUpperCase() === 'ENS HO DEUEN')
          );
          if (found) nodeId = found.id;
        }

        if (!nodeId) {
          const resNodes = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/nodes?userapp=${userapp}`, { headers });
          const list = resNodes.data || [];
          const found = list.find(n =>
            n.codi === 'PEND_ENS_HO_DEUEN' ||
            (n.conceptePath && n.conceptePath.toUpperCase() === 'PENDENT/PEND_ENS_HO_DEUEN') ||
            (n.nom && n.nom.toUpperCase() === 'ENS HO DEUEN')
          );
          if (found) nodeId = found.id;
        }

        if (nodeId) {
          const res = await axios.get(`${config.apiBaseUrl}/v1/conceptes-graph/node/${nodeId}/moviments?userapp=${userapp}`, { headers });
          return Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        console.warn('Avís al carregar moviments per al node Ens Ho Deuen:', e);
      }
      return [];
    };

    Promise.allSettled([
      axios.get(`${config.apiBaseUrl}/v1/concmovs/tractamentmanual/${userapp}`, { headers }),
      axios.get(`${config.apiBaseUrl}/v1/concmovs/pendentsvalidar/${userapp}`, { headers }),
      getEnsHoDeuenMovs()
    ])
      .then(([resTractament, resPendentsValidar, resEnsHoDeuen]) => {
        const tractamentMovs = resTractament.status === 'fulfilled' && Array.isArray(resTractament.value?.data)
          ? resTractament.value.data
          : [];
        const pendentsValidarMovs = resPendentsValidar.status === 'fulfilled' && Array.isArray(resPendentsValidar.value?.data)
          ? resPendentsValidar.value.data
          : [];
        const ensHoDeuenMovs = resEnsHoDeuen.status === 'fulfilled' && Array.isArray(resEnsHoDeuen.value)
          ? resEnsHoDeuen.value
          : [];

        if (resTractament.status === 'rejected') {
          console.error('Error al carregar tractamentmanual:', resTractament.reason);
          onError(resTractament.reason, 'carregar dades de pendents');
        }

        if (resPendentsValidar.status === 'rejected') {
          console.warn('Avís al carregar pendentsvalidar (potser buit o no disponible):', resPendentsValidar.reason);
        }

        onData({
          data: {
            tractamentMovs,
            pendentsValidarMovs,
            ensHoDeuenMovs
          }
        });
      })
      .catch((err) => {
        onError(err, 'carregar dades de pendents');
      })
      .finally(() => onFinally());

    fetchListConceptes(
      jwtToken,
      userapp,
      (data) => {
        setListConceptes(data);
      },
      (err) => {
        onError(err, 'carregar conceptes');
      }
    );
  };

  const handleReload = () => {
    setId(prev => prev + 1);
  };

  const handleReevaluatePendents = async () => {
    if (!userapp) return;
    try {
      setIsReevaluating(true);
      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};
      const res = await axios.get(
        `${config.apiBaseUrl}/v1/syncbanc/updateconceptespendents/${userapp}`,
        { headers }
      );

      const data = res.data;
      let count = 0;
      let reportHtml = '';
      let reportSummary = '';

      if (typeof data === 'object' && data !== null) {
        count = Number(data.updatedCount) || 0;
        reportHtml = data.reportHtml || '';
        reportSummary = data.reportSummary || '';
      } else {
        const parsed = parseInt(data, 10);
        count = isNaN(parsed) ? 0 : parsed;
      }

      if (count > 0) {
        setSnackbar({
          open: true,
          message: `S'han actualitzat ${count} moviment(s) pendents correctament!`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Re-avaluació completada: no s’han trobat nous moviments a categoritzar.',
          severity: 'info'
        });
      }

      if (reportHtml || reportSummary) {
        setAmazonReport({
          open: count > 0, // Si s'han actualitzat moviments, obrim automàticament l'informe per a la seva revisió
          reportHtml,
          reportSummary,
          updatedCount: count
        });
      }

      handleReload();
    } catch (err) {
      console.error('Error al re-avaluar moviments pendents:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error en connectar amb el servidor';
      setSnackbar({
        open: true,
        message: `Error al re-avaluar pendents: ${errorMsg}`,
        severity: 'error'
      });
    } finally {
      setIsReevaluating(false);
    }
  };

  return (
    <DataLoader id={id} fetchFunction={fetchFunction}>
      {(data, onError) => {
        const rawTractament = data?.tractamentMovs || (Array.isArray(data) ? data : []);
        const rawPendentsValidar = data?.pendentsValidarMovs || [];
        const rawEnsHoDeuen = data?.ensHoDeuenMovs || [];

        // Unificar possibles conceptes PENDENT-VALIDAR de tractamentMovs amb els de pendentsvalidar
        const extraValidar = rawTractament.filter(m => m.estat === "PENDENT-VALIDAR");
        const listPendentsValidar = [
          ...rawPendentsValidar,
          ...extraValidar.filter(em => !rawPendentsValidar.some(p => p.id === em.id))
        ];

        // Pestanya 4: Pendents de Ens ho Deuen (Concepte jeràrquic PENDENT/PEND_ENS_HO_DEUEN o concepte clàssic ENS HO DEUEN)
        const tractamentEnsHoDeuen = rawTractament.filter(m => {
          if (m.estat === "PENDENT-VALIDAR") return false;
          const c = (m.concepte || '').trim().toUpperCase();
          const node = typeof m.nodeFulla === 'number' ? conceptesNodesMap?.get(m.nodeFulla) : m.nodeFulla;
          const codi = (node?.codi || '').trim().toUpperCase();
          const path = (m.conceptePath || '').trim().toUpperCase();
          return (
            path === "PENDENT/PEND_ENS_HO_DEUEN" ||
            codi === "PEND_ENS_HO_DEUEN" ||
            c === "PENDENT/PEND_ENS_HO_DEUEN" ||
            c === "PEND_ENS_HO_DEUEN" ||
            c === "ENS HO DEUEN"
          );
        });

        // Combinar els moviments d'Ens ho Deuen del graf i de tractamentmanual sense duplicats
        const mapEnsHoDeuen = new Map();
        [...tractamentEnsHoDeuen, ...rawEnsHoDeuen].forEach(m => {
          if (m && m.id && !mapEnsHoDeuen.has(m.id)) {
            mapEnsHoDeuen.set(m.id, m);
          }
        });
        const listEnsHoDeuen = Array.from(mapEnsHoDeuen.values());

        // Pestanya 1: Pendents de Concepte (PENDENT), excloent els que són PEND_ENS_HO_DEUEN i els PENDENT-VALIDAR
        const listPendents = rawTractament.filter(m => {
          if (m.estat === "PENDENT-VALIDAR") return false;
          const c = (m.concepte || '').trim().toUpperCase();
          const node = typeof m.nodeFulla === 'number' ? conceptesNodesMap?.get(m.nodeFulla) : m.nodeFulla;
          const codi = (node?.codi || '').trim().toUpperCase();
          const path = (m.conceptePath || '').trim().toUpperCase();

          const isEnsHoDeuen = (
            path === "PENDENT/PEND_ENS_HO_DEUEN" ||
            codi === "PEND_ENS_HO_DEUEN" ||
            c === "PENDENT/PEND_ENS_HO_DEUEN" ||
            c === "PEND_ENS_HO_DEUEN" ||
            c === "ENS HO DEUEN"
          );
          if (isEnsHoDeuen) return false;

          const isDesglos = c === "PDT DESGLOSSAR" || c === "PDT. DESGLOSSAR" || codi === "PDT_DESGLOSSAR";
          if (isDesglos) return false;

          return c === "PENDENT" || codi === "PENDENT";
        });

        // Pestanya 2: Pendents de Desglossar (PDT DESGLOSSAR)
        const listDesglos = rawTractament.filter(m => {
          const c = (m.concepte || '').trim().toUpperCase();
          const node = typeof m.nodeFulla === 'number' ? conceptesNodesMap?.get(m.nodeFulla) : m.nodeFulla;
          const codi = (node?.codi || '').trim().toUpperCase();
          return c === "PDT DESGLOSSAR" || c === "PDT. DESGLOSSAR" || codi === "PDT_DESGLOSSAR";
        });

        return (
          <>
            <Grid
              size={12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '10px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconButton onClick={() => gotoDashboard()}>
                  <ArrowBack />
                </IconButton>

                <Typography variant="h5">
                  Tractament Manual
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {(amazonReport.reportHtml || amazonReport.reportSummary) && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<AssessmentIcon />}
                    onClick={() => setAmazonReport(prev => ({ ...prev, open: true }))}
                    sx={{ fontWeight: 600 }}
                  >
                    Veure informe d'Amazon
                  </Button>
                )}

                <Tooltip title="Executa les regles avançades d'autoconcepte sobre els moviments PENDENT">
                  <span>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={isReevaluating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                      onClick={handleReevaluatePendents}
                      disabled={isReevaluating}
                      sx={{ fontWeight: 600 }}
                    >
                      {isReevaluating ? 'Re-avaluant...' : 'Re-avaluar amb regles'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: '10px' }}>
              {/* Tabbar amb les 4 pestanyes requerides */}
              <Grid size={12}>
                <Paper>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    centered
                  >
                    <Tab label={`Concepte Pendent (${listPendents?.length || 0})`} />
                    <Tab label={`Pdt.Desgloçament (${listDesglos?.length || 0})`} />
                    <Tab label={`Pendents de Validar (${listPendentsValidar?.length || 0})`} />
                    <Tab label={`Pendents de Ens ho Deuen (${listEnsHoDeuen?.length || 0})`} />
                  </Tabs>

                  {/* Pestanya 1: Pendents de Concepte */}
                  {tabValue === 0 && (
                    <MovimentsListEdit
                      moviments={listPendents}
                      listConceptes={listConceptes}
                      onError={onError}
                      onEditComplete={handleReload}
                      emptyMessage="No hi ha cap moviment pendent de concepte. Tot està categoritzat!"
                    />
                  )}

                  {/* Pestanya 2: Pendents de Desglossar */}
                  {tabValue === 1 && (
                    <MovimentsListEdit
                      moviments={listDesglos}
                      listConceptes={listConceptes}
                      onError={onError}
                      onEditComplete={handleReload}
                      emptyMessage="No hi ha cap moviment pendent de desglossar."
                    />
                  )}

                  {/* Pestanya 3: Pendents de Validar */}
                  {tabValue === 2 && (
                    <MovimentsPendentsValidarList
                      moviments={listPendentsValidar}
                      listConceptes={listConceptes}
                      onError={onError}
                      onEditComplete={handleReload}
                      userapp={userapp}
                    />
                  )}

                  {/* Pestanya 4: Pendents de Ens ho Deuen */}
                  {tabValue === 3 && (
                    <MovimentsListEdit
                      moviments={listEnsHoDeuen}
                      listConceptes={listConceptes}
                      onError={onError}
                      onEditComplete={handleReload}
                      emptyMessage="No hi ha cap moviment pendent a 'Ens ho Deuen'."
                    />
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Modal d'Informe d'Amazon */}
            <AmazonReportModal
              open={amazonReport.open}
              onClose={() => setAmazonReport(prev => ({ ...prev, open: false }))}
              reportHtml={amazonReport.reportHtml}
              reportSummary={amazonReport.reportSummary}
              updatedCount={amazonReport.updatedCount}
            />

            {/* Snackbar de Notificacions */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={5000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: '100%', boxShadow: 3 }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </>
        );
      }}
    </DataLoader>
  );
};

export default TractamentManual;
